/**
 * The task board link that opens Edit task on one task: `?taskId=<id>&editTask=1`.
 *
 * `taskId` alone keeps its old meaning: Kanban highlights that card (task-assigned notifications
 * link to it). The extra `editTask` flag asks the board to open the edit dialog once; Kanban then
 * drops the flag with `router.replace`, so the highlight stays, a reload does not reopen the
 * dialog, and the same link opens it again next time (no duplicated navigation).
 * Used by the AVA Assistant ("Open in task board"), Kanban.vue and the Kanban → Gantt redirect
 * (task.routes.js); Gantt already opens Edit task from `?taskId`.
 * No imports, so `npm run test:unit` (node --test) can load it directly.
 */

export const TASK_QUERY = Object.freeze({ TASK_ID: 'taskId', OPEN_EDIT: 'editTask' });
export const OPEN_EDIT_ON = '1';

/**
 * @param {string|number} taskId
 * @returns {{ taskId: string, editTask: string }}
 */
export function taskEditQuery(taskId) {
  return { [TASK_QUERY.TASK_ID]: String(taskId), [TASK_QUERY.OPEN_EDIT]: OPEN_EDIT_ON };
}

/**
 * The task to open Edit task on, or null when the query does not ask for it.
 * @param {Record<string, unknown>|null|undefined} query a route query
 * @returns {string|null}
 */
export function taskToEdit(query) {
  const q = query || {};
  if (q[TASK_QUERY.OPEN_EDIT] !== OPEN_EDIT_ON) return null;
  const id = q[TASK_QUERY.TASK_ID];
  return typeof id === 'string' && id ? id : null;
}

/**
 * A copy of the query without the open-edit flag.
 * @param {Record<string, unknown>|null|undefined} query
 * @returns {Record<string, unknown>}
 */
export function withoutEditFlag(query) {
  const rest = { ...(query || {}) };
  delete rest[TASK_QUERY.OPEN_EDIT];
  return rest;
}

/**
 * What a Kanban → Gantt redirect carries: an edit request (task id + flag) and nothing else, so a
 * share-intent `focusTaskId` or a plain notification `taskId` redirects exactly as before.
 * @param {Record<string, unknown>|null|undefined} query
 * @returns {{ taskId: string, editTask: string }|undefined}
 */
export function redirectedEditQuery(query) {
  const id = taskToEdit(query);
  return id ? taskEditQuery(id) : undefined;
}
